import CloseIcon from "@mui/icons-material/Close";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { useVideoPreviewUrls, videoFilePreviewKey } from "../../hooks/useVideoPreviewUrls";
import { uploadBasename, uploadSourceLabel } from "../../utils/uploadFileUtils";
import { VideoMetadataThumbnail } from "../shared/VideoMetadataThumbnail";

export type VideoUploadFileGridProps = {
  files: File[];
  busy: boolean;
  deletable: boolean;
  onRemoveFile: (index: number) => void;
  /** Compact horizontal strip for step 1+ file bar */
  compact?: boolean;
};

export function VideoUploadFileGrid({
  files,
  busy,
  deletable,
  onRemoveFile,
  compact = false,
}: VideoUploadFileGridProps) {
  const urlByKey = useVideoPreviewUrls(files);

  return (
    <Box
      className={
        compact
          ? "dm-upload-video-grid dm-upload-video-grid--compact"
          : "dm-upload-video-grid"
      }
    >
      {files.map((file, idx) => {
        const key = videoFilePreviewKey(file);
        const previewUrl = urlByKey[key];
        const base = uploadBasename(file);
        const source = uploadSourceLabel(file);

        return (
          <Box
            key={key}
            className="dm-upload-video-card"
            title={source !== base ? `${base} ← ${source}` : base}
          >
            <Box className="dm-upload-video-card__thumb">
              {previewUrl ? (
                <VideoMetadataThumbnail
                  src={previewUrl}
                  videoClassName="dm-upload-video-card__video"
                  fallback={
                    <Box className="dm-upload-video-card__placeholder">
                      <VideocamOutlinedIcon color="action" />
                    </Box>
                  }
                />
              ) : (
                <Box className="dm-upload-video-card__placeholder">
                  <VideocamOutlinedIcon color="action" />
                </Box>
              )}
              {busy && deletable ? (
                <Box className="dm-upload-video-card__busy">
                  <CircularProgress size={28} color="inherit" />
                </Box>
              ) : null}
              {deletable && !busy ? (
                <IconButton
                  type="button"
                  size="small"
                  className="dm-upload-video-card__remove"
                  aria-label={`Remove ${base}`}
                  onClick={() => onRemoveFile(idx)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
            <Typography
              variant="caption"
              className="dm-upload-video-card__name"
              noWrap
            >
              {base}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
